import React, { useState } from 'react';
import { C, ACCENT } from '../../theme';
import { BookOpen, FileText } from 'lucide-react';

const GLOSSARY = [
  {
    category: 'Volume',
    terms: [
      { term: 'Budget (€)', def: 'The money allocated to a flight or plan.' },
      { term: 'Impressions', def: 'The number of times an ad was served.' },
      { term: 'Viewable Impressions (VImpressions)', def: 'The number of impressions that actually met the industry standard for being seen — not just served.' },
      { term: 'Viewability %', def: 'The share of impressions that were viewable, out of all impressions served.' },
      { term: 'Views', def: 'Completed video views, where the format supports them.' },
      { term: 'Clicks', def: 'The number of times someone clicked an ad.' },
      { term: 'CTR % (Click-Through Rate)', def: 'Clicks as a share of impressions — how often people who saw the ad actually clicked it.' },
      { term: 'Conversions', def: 'An estimate of actions taken as a result of the campaign, based on an assumed conversion rate for that platform.' },
    ],
  },
  {
    category: 'Cost',
    terms: [
      { term: 'CPM € (Cost Per Mille)', def: 'The cost to deliver 1,000 impressions.' },
      { term: 'vCPM € (Viewable CPM)', def: 'The cost to deliver 1,000 viewable impressions — a stricter, more meaningful cost figure than plain CPM.' },
      { term: 'CPC € (Cost Per Click)', def: 'The average cost paid per click.' },
      { term: 'Discount %', def: "A negotiated reduction off a platform's standard rate." },
      { term: 'Agency Fee %', def: "The agency's fee, either added on top of media cost or included within it, depending on how the flight is set up." },
    ],
  },
  {
    category: 'Reach',
    terms: [
      { term: 'Reach % (vRCH%)', def: 'The share of your target audience reached at least once, accumulated across the full length of a flight or plan. This is the main reach figure shown across the platform.' },
      { term: 'Reach % / week', def: "A single week's own reach, on its own — not added up with other weeks. Shown on the Flowchart's weekly rows; don't confuse it with the cumulative Reach % figure above, which is what most summary views show." },
      { term: 'Real Users / Viewable Real Users', def: 'An estimate of the actual number of people (not ad impressions) reached by the campaign.' },
    ],
  },
  {
    category: 'Frequency',
    terms: [
      { term: 'Avg Frq/wk', def: 'The average number of times a person is shown the ad per week.' },
    ],
  },
  {
    category: 'Share',
    terms: [
      { term: 'Share of Budget / Share of Impressions (SHR%)', def: "Within a single plan, each platform's portion of that plan's total budget or impressions." },
      { term: 'Share (Agency Summary)', def: 'Within Agency Summary, each client\'s or platform\'s portion of the agency\'s total spend — a different scope from the plan-level Share above, so the same word means "share of a bigger pool" here.' },
    ],
  },
  {
    category: 'Planning',
    terms: [
      { term: 'Target Reach %', def: 'The reach goal a planner sets when building a plan around a specific objective.' },
    ],
  },
];

function tabBtnStyle(active) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${active ? ACCENT : '#dce8f0'}`,
    background: active ? `${ACCENT}15` : 'transparent',
    color: active ? ACCENT : '#4a6a8a',
    fontSize: 13, fontWeight: active ? 700 : 500,
  };
}

function GlossaryTab() {
  return (
    <div>
      {GLOSSARY.map(section => (
        <div key={section.category} style={{ marginBottom: 34 }}>
          <h2 style={{ fontSize: 12, fontWeight: 700, color: '#6a8aaa', textTransform: 'uppercase', letterSpacing: '.08em', margin: '0 0 14px', paddingBottom: 10, borderBottom: '1px solid #dce8f0' }}>
            {section.category}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {section.terms.map(t => (
              <div key={t.term}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2a3a', marginBottom: 4 }}>{t.term}</div>
                <div style={{ fontSize: 13.5, color: '#4a6a8a', lineHeight: 1.55, maxWidth: 680 }}>{t.def}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function UserManualTab() {
  return (
    <div style={C.empty}>
      User Manual — coming soon.<br />
      Step-by-step guides for every feature, with screenshots, are in progress.
    </div>
  );
}

export default function UserGuidePage() {
  const [tab, setTab] = useState('glossary');

  return (
    <div style={C.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={C.title}>User Guide</h1>
        <p style={{ color: '#8aa0b8', fontSize: 13, marginTop: 4 }}>Definitions and reference material for working in PlanIQ.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <button style={tabBtnStyle(tab === 'glossary')} onClick={() => setTab('glossary')}>
          <BookOpen size={14} /> Glossary
        </button>
        <button style={tabBtnStyle(tab === 'manual')} onClick={() => setTab('manual')}>
          <FileText size={14} /> User Manual
        </button>
      </div>

      {tab === 'glossary' ? <GlossaryTab /> : <UserManualTab />}
    </div>
  );
}
